import { useCallback, useEffect, useRef, useState } from 'react';
import type { CheckDto, CheckItemDto, CheckState, EquipmentFileDto } from '@app/shared';
import { hasContent, StepContent } from './Steps';
import { api, fetchImageUrl } from '../api';
import { platform } from '../platform';
import { useApp } from '../state';
import { clock, Icon, Sheet, when } from '../ui';
import { SubHead } from './admin/SubHead';

/** Фото с телефона — уменьшаем до 1600 px по длинной стороне (JPEG), чтобы грузилось быстро и в цеху. */
async function shrinkPhoto(f: File): Promise<Blob> {
  try {
    const img = await createImageBitmap(f);
    const k = Math.min(1, 1600 / Math.max(img.width, img.height));
    const c = document.createElement('canvas');
    c.width = Math.round(img.width * k);
    c.height = Math.round(img.height * k);
    c.getContext('2d')!.drawImage(img, 0, 0, c.width, c.height);
    const b = await new Promise<Blob | null>((r) => c.toBlob(r, 'image/jpeg', 0.85));
    return b ?? f;
  } catch {
    return f;
  }
}

/** Фото к замечанию: через API с авторизацией (ссылки наружу нет). */
function CheckPhoto({ checkId, photoId, onOpen }: { checkId: string; photoId: string; onOpen: (url: string) => void }) {
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    let u: string | null = null;
    fetchImageUrl(`/checks/${checkId}/photos/${photoId}`)
      .then((x) => setUrl((u = x)))
      .catch(() => {});
    return () => {
      if (u) URL.revokeObjectURL(u);
    };
  }, [checkId, photoId]);
  return (
    <button type="button" className="photo" aria-label="Фото к замечанию" onClick={() => url && onOpen(url)}>
      {url ? <img src={url} alt="" /> : <span className="skeleton" style={{ minHeight: 0, height: '100%' }} />}
    </button>
  );
}

const STATUS_TEXT: Record<CheckDto['status'], string> = { open: 'идёт', ok: 'всё в порядке', issues: 'есть замечания' };

/**
 * Проверка по этапу («Перед началом работы»): каждый пункт — «В порядке» или «Не в порядке» с комментарием и фото.
 * Тот, кто начал, отмечает; остальные (мастер, администратор) видят результат.
 */
export function CheckRun({ id }: { id: string }) {
  const { fail, toast, close, me, open } = useApp();
  const [c, setC] = useState<CheckDto | null>(null);
  const [busy, setBusy] = useState(false);
  const [big, setBig] = useState<string | null>(null);
  const [notes, setNotes] = useState<Record<string, string>>({});
  const [how, setHow] = useState<CheckItemDto | null>(null);
  const openHow = (i: CheckItemDto) => setHow(i);
  const photoFor = useRef<string | null>(null);
  const photoInput = useRef<HTMLInputElement>(null);

  const load = useCallback(async () => {
    try {
      const x = await api.check(id);
      setC(x);
      setNotes(Object.fromEntries(x.items.map((i) => [i.id, i.note ?? ''])));
    } catch (e) {
      fail(e);
    }
  }, [id, fail]);
  useEffect(() => void load(), [load]);

  if (!c) {
    return (
      <div className="stack">
        <SubHead title="Проверка" />
        <div className="skeleton" />
        <div className="skeleton" />
      </div>
    );
  }

  const mine = c.status === 'open' && c.employeeId === me.employee?.id;
  const done = c.items.filter((i) => i.state).length;
  const ready = done === c.items.length && c.items.every((i) => i.state !== 'bad' || (notes[i.id] ?? '').trim());

  async function mark(item: CheckItemDto, state: CheckState) {
    const next = item.state === state ? null : state;
    platform.haptic('light');
    // Сразу на экране, потом на сервере.
    setC((cur) => (cur ? { ...cur, items: cur.items.map((i) => (i.id === item.id ? { ...i, state: next } : i)) } : cur));
    try {
      setC(await api.markCheckItem(c!.id, item.id, { state: next, note: next === 'bad' ? (notes[item.id] ?? '').trim() || null : item.note }));
    } catch (e) {
      fail(e);
      void load();
    }
  }

  async function saveNote(item: CheckItemDto) {
    const note = (notes[item.id] ?? '').trim();
    if (note === (item.note ?? '')) return;
    try {
      setC(await api.markCheckItem(c!.id, item.id, { state: item.state, note: note || null }));
    } catch (e) {
      fail(e);
    }
  }

  async function addPhoto(f: File | undefined) {
    const itemId = photoFor.current;
    if (photoInput.current) photoInput.current.value = '';
    if (!f || !itemId) return;
    setBusy(true);
    try {
      setC(await api.uploadCheckPhoto(c!.id, itemId, await shrinkPhoto(f)));
      platform.haptic('success');
    } catch (e) {
      fail(e);
    } finally {
      setBusy(false);
    }
  }

  async function finish() {
    setBusy(true);
    try {
      // Комментарии, которые ещё не ушли (поле не теряло фокус).
      for (const i of c!.items) if (i.state === 'bad' && (notes[i.id] ?? '').trim() !== (i.note ?? '')) await saveNote(i);
      const r = await api.finishCheck(c!.id);
      platform.haptic(r.status === 'ok' ? 'success' : 'warning');
      toast(r.status === 'ok' ? 'Проверка пройдена — всё в порядке' : 'Проверка завершена, замечания отправлены руководителю');
      close();
    } catch (e) {
      fail(e);
    } finally {
      setBusy(false);
    }
  }

  async function cancel() {
    if (!(await platform.confirm('Отменить проверку? Отметки не сохранятся.'))) return;
    try {
      await api.cancelCheck(c!.id);
      close();
    } catch (e) {
      fail(e);
    }
  }

  return (
    <div className="stack">
      <SubHead title={c.title} />
      <button type="button" className="row plain small muted" style={{ marginTop: -6, textAlign: 'left' }} onClick={() => open({ name: 'unit', id: c.equipmentId })}>
        <span className="grow">
          {c.equipmentName} · {c.equipmentCode}
        </span>
      </button>
      {mine ? (
        <div className="card flat small">Отметьте каждый пункт. Если что-то не в порядке — опишите и, если нужно, сфотографируйте: руководитель получит сообщение.</div>
      ) : (
        <div className={`card flat small check-summary ${c.status}`}>
          <b>{STATUS_TEXT[c.status]}</b> · {c.employeeName} · {when(c.startedAt)}
          {c.finishedAt ? `–${clock(c.finishedAt)}` : ''}
        </div>
      )}

      <ol className="check-list">
        {c.items.map((i, n) => (
          <li key={i.id} className={`check-item ${i.state ?? ''}`}>
            <div className="row" style={{ alignItems: 'flex-start', gap: 10 }}>
              <span className="check-num">{n + 1}</span>
              <span className="grow">{i.text}</span>
              {hasContent(i) && (
                <button type="button" className="icon-btn" aria-label={`Как сделать: ${i.text}`} onClick={() => void openHow(i)}>
                  <Icon name="info" size={16} />
                </button>
              )}
              {!mine && i.state && (
                <span className={`badge ${i.state === 'ok' ? 'outline' : 'attention'}`}>{i.state === 'ok' ? 'в порядке' : 'не в порядке'}</span>
              )}
            </div>
            {mine && (
              <div className="grid-half">
                <button type="button" className="btn btn-sm check-ok" aria-pressed={i.state === 'ok'} onClick={() => void mark(i, 'ok')}>
                  <Icon name="check" size={16} />В порядке
                </button>
                <button type="button" className="btn btn-sm check-bad" aria-pressed={i.state === 'bad'} onClick={() => void mark(i, 'bad')}>
                  <Icon name="alert" size={16} />
                  Не в порядке
                </button>
              </div>
            )}
            {i.state === 'bad' &&
              (mine ? (
                <div className="stack" style={{ gap: 8 }}>
                  <textarea
                    className="input"
                    rows={2}
                    aria-label={`Что не в порядке: ${i.text}`}
                    placeholder="Что не в порядке? Например: утечка воздуха у клапана"
                    value={notes[i.id] ?? ''}
                    onChange={(e) => setNotes((m) => ({ ...m, [i.id]: e.target.value }))}
                    onBlur={() => void saveNote(i)}
                  />
                  <div className="row wrap" style={{ gap: 8 }}>
                    {i.photoIds.map((p) => (
                      <span key={p} className="check-thumb">
                        <CheckPhoto checkId={c.id} photoId={p} onOpen={setBig} />
                        <button
                          type="button"
                          className="icon-btn"
                          aria-label="Удалить фото"
                          onClick={() => void api.deleteCheckPhoto(c.id, p).then(setC).catch(fail)}
                        >
                          <Icon name="close" size={14} />
                        </button>
                      </span>
                    ))}
                    <button
                      type="button"
                      className="btn btn-ghost btn-sm"
                      disabled={busy}
                      onClick={() => {
                        photoFor.current = i.id;
                        photoInput.current?.click();
                      }}
                    >
                      <Icon name="camera" size={16} />
                      Фото
                    </button>
                  </div>
                </div>
              ) : (
                (i.note || i.photoIds.length > 0) && (
                  <div className="stack" style={{ gap: 8 }}>
                    {i.note && <p className="check-note">{i.note}</p>}
                    {i.photoIds.length > 0 && (
                      <div className="row wrap" style={{ gap: 8 }}>
                        {i.photoIds.map((p) => (
                          <span key={p} className="check-thumb">
                            <CheckPhoto checkId={c.id} photoId={p} onOpen={setBig} />
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                )
              ))}
          </li>
        ))}
      </ol>

      <input ref={photoInput} type="file" accept="image/*" capture="environment" hidden onChange={(e) => void addPhoto(e.target.files?.[0])} />

      {mine && (
        <>
          <button type="button" className="btn btn-primary btn-block" disabled={busy || !ready} onClick={() => void finish()}>
            {ready ? 'Завершить проверку' : `Отмечено ${done} из ${c.items.length}`}
          </button>
          <button type="button" className="btn btn-ghost btn-block" disabled={busy} onClick={() => void cancel()}>
            Отменить
          </button>
        </>
      )}

      {how && (
        <Sheet title="Как сделать" onClose={() => setHow(null)}>
          <p className="step-title">{how.text}</p>
          <StepContent equipmentId={c.equipmentId} details={how.details} fileIds={how.fileIds} files={(how.files ?? []) as EquipmentFileDto[]} />
        </Sheet>
      )}

      {big && (
        <Sheet title="Фото" onClose={() => setBig(null)}>
          <img src={big} alt="" style={{ width: '100%', borderRadius: 12 }} />
        </Sheet>
      )}
    </div>
  );
}
